import { useCallback } from "react"
import { useParams } from "react-router-dom"

import { getEncodedUrlEventParam, sendEvent } from "@/lib/ts/event"

enum Event {
	CLICK_PREV_SEVEN_DAYS = "click_prev_seven_days",
	CLICK_NEXT_SEVEN_DAYS = "click_next_seven_days",
	FETCHED_RESERVABLE_SLOTS = "fetched_reservable_slots",
	CLICK_RERESERVABLE_SLOT = "click_rereservable_slot",
	OPEN_CREATING_RESERVATION_POPUP = "open_creating_reservation_popup",
	CLOSE_CREATING_RESERVATION_POPUP = "close_creating_reservation_popup",
	FETCHED_CANCEL_DATA = "fetched_cancel_data",
	CONFIRM_CREATING_RERESERVATION = "confirm_creating_rereservation",
	OPEN_DIFFERENT_STAFFS_POPUP = "open_different_staffs_popup",
	CLICK_DIFFERENT_STAFF_POPUP = "click_different_staff_popup",
	CONFIRM_CREATING_RERESERVATION_IN_DIFFERENT_STAFFS = "confirm_creating_rereservation_in_different_staffs",
	CLOSE_DIFFERENT_STAFFS_POPUP = "close_different_staffs_popup",
	OPEN_ABORT_POPUP = "open_abort_popup",
	CLICK_RECOMMENDED_SLOT_IN_ABORT_POPUP = "click_recommended_slot_in_abort_popup",
	CONFIRM_CREATING_RERESERVATION_IN_ABORT_POPUP = "confirm_creating_rereservation_in_abort_popup",
	CLICK_ABORT_BUTTON = "click_abort_button",
	CLOSE_ABORT_POPUP = "close_abort_popup",
	OPEN_ALREADY_RESERVED_POPUP = "open_already_reserved_popup",
	CLOSE_ALREADY_RESERVED_POPUP = "close_already_reserved_popup",
}

// 請求書 (payment/{payment_id}) 画面用の event
enum PaymentEvent {
	OPEN_PAYMENT_PAGE = "open_payment_page",
	// キャンセル料お支払い 10 万円ルーレット (請求側) の導線. 「画面を開いたらすぐルーレットを出す」A/B テストの集計用.
	// A/B の群は fe_event には載せず、payment_id から lottery_tickets.roulette_display を引いて集計する.
	// 回し終えたことも fe_event では送らない (lottery_tickets.shown_time にサーバが記録するのでそちらを見る).
	SHOW_CANCEL_FEE_LOTTERY_BANNER = "show_cancel_fee_lottery_banner",
	OPEN_CANCEL_FEE_LOTTERY_ROULETTE = "open_cancel_fee_lottery_roulette",
	CLOSE_CANCEL_FEE_LOTTERY_ROULETTE_WITHOUT_SPINNING = "close_cancel_fee_lottery_roulette_without_spinning",
}

// ルーレットのモーダルが開いたきっかけ. auto = 画面を開いた直後に自動で / click = バナーのボタン
export type CancelFeeLotteryRouletteOpenTrigger = "auto" | "click"

// 請求書画面は cancel_id を URL に持たないため、user_id には payment_id を入れる
export const usePaymentEvent = () => {
	const paymentID = useParams().payment_id ?? ""

	const openPaymentPage = useCallback(() => {
		sendEvent(paymentID, PaymentEvent.OPEN_PAYMENT_PAGE, {
			payment_id: paymentID,
			// 請求書 URL には claim_count (何回目の請求通知か) が base64url で埋め込まれている
			claim_count: getEncodedUrlEventParam("claim_count"),
		})
	}, [paymentID])

	// ルーレットのバナーを画面に描画した (CancelFeeLotteryBanner のマウント時). A/B の母数 (見た人) の集計用.
	// API が lottery を返しても支払い済み (完了画面へ遷移) や請求取消ではバナーを出さないので、
	// API 応答時でなくバナーの描画時に送って「見た人」だけを母数にする.
	const showCancelFeeLotteryBanner = useCallback(() => {
		sendEvent(paymentID, PaymentEvent.SHOW_CANCEL_FEE_LOTTERY_BANNER, {
			payment_id: paymentID,
		})
	}, [paymentID])

	const openCancelFeeLotteryRoulette = useCallback(
		(trigger: CancelFeeLotteryRouletteOpenTrigger) => {
			sendEvent(paymentID, PaymentEvent.OPEN_CANCEL_FEE_LOTTERY_ROULETTE, {
				payment_id: paymentID,
				trigger,
			})
		},
		[paymentID]
	)

	// 回さずにモーダルを閉じた. 自動で開いた群が「邪魔だ」と閉じた率を見るため.
	const closeCancelFeeLotteryRouletteWithoutSpinning = useCallback(() => {
		sendEvent(
			paymentID,
			PaymentEvent.CLOSE_CANCEL_FEE_LOTTERY_ROULETTE_WITHOUT_SPINNING,
			{ payment_id: paymentID }
		)
	}, [paymentID])

	return {
		openPaymentPage,
		showCancelFeeLotteryBanner,
		openCancelFeeLotteryRoulette,
		closeCancelFeeLotteryRouletteWithoutSpinning,
	}
}

export const useEvent = () => {
	const cancelID = useParams().cancel_id ?? ""

	const clickPrevSevenDays = useCallback(
		(startTime: Date) => {
			sendEvent(cancelID, Event.CLICK_PREV_SEVEN_DAYS, {
				start_time: startTime,
			})
		},
		[cancelID]
	)

	const clickNextSevenDays = useCallback(
		(startTime: Date) => {
			sendEvent(cancelID, Event.CLICK_NEXT_SEVEN_DAYS, {
				start_time: startTime,
			})
		},
		[cancelID]
	)

	const fetchedReservableSlots = useCallback(
		(
			startTime: Date,
			reqId: string,
			tookMilliseconds: number,
			needAnyReservableEquipmentToCreateReservation: boolean,
			needSameEquipmentAsPreviousToCreateReservation: boolean,
			staggeredSchedulingEnabled: boolean,
			cacheReqId?: string // cache された値が返ってくる場合、cache 作成時の req_id が返ってくるのでそれを event に含める
		) => {
			sendEvent(cancelID, Event.FETCHED_RESERVABLE_SLOTS, {
				start_time: startTime,
				req_id: reqId,
				took_milliseconds: tookMilliseconds,
				need_any_reservable_equipment_to_create_reservation:
					needAnyReservableEquipmentToCreateReservation,
				need_same_equipment_as_previous_to_create_reservation:
					needSameEquipmentAsPreviousToCreateReservation,
				staggered_scheduling_enabled: staggeredSchedulingEnabled,
				cache_req_id: cacheReqId,
			})
		},
		[cancelID]
	)

	const clickRereservableSlot = useCallback(
		(startTime: Date, staffId: string) => {
			sendEvent(cancelID, Event.CLICK_RERESERVABLE_SLOT, {
				start_time: startTime,
				staff_id: staffId,
			})
		},
		[cancelID]
	)

	const openCreatingReservationPopup = useCallback(
		(startTime: Date, staffId: string) => {
			sendEvent(cancelID, Event.OPEN_CREATING_RESERVATION_POPUP, {
				start_time: startTime,
				staff_id: staffId,
			})
		},
		[cancelID]
	)

	const closeCreatingReservationPopup = useCallback(() => {
		sendEvent(cancelID, Event.CLOSE_CREATING_RESERVATION_POPUP, {})
	}, [cancelID])

	const fetchedCancelData = useCallback(
		(
			allowsDifferentStaffReservation: boolean,
			visitCount: number,
			enableAbortEncouragingReservationButton: boolean,
			enableSlotCaching: boolean,
			treatableStaffIds: string[]
		) => {
			sendEvent(cancelID, Event.FETCHED_CANCEL_DATA, {
				allows_different_staff_reservation: allowsDifferentStaffReservation,
				visit_count: visitCount,
				enable_abort_encouraging_reservation_button:
					enableAbortEncouragingReservationButton,
				enable_slot_caching: enableSlotCaching,
				treatable_staff_ids: treatableStaffIds,
			})
		},
		[cancelID]
	)

	const confirmCreatingRereservation = useCallback(
		(startTime: Date, staffId: string) => {
			sendEvent(cancelID, Event.CONFIRM_CREATING_RERESERVATION, {
				start_time: startTime,
				staff_id: staffId,
			})
		},
		[cancelID]
	)

	const openDifferentStaffsPopup = useCallback(
		(origin_staff_id: string, different_staff_ids: string[]) => {
			sendEvent(cancelID, Event.OPEN_DIFFERENT_STAFFS_POPUP, {
				origin_staff_id: origin_staff_id,
				different_staff_ids: different_staff_ids,
			})
		},
		[cancelID]
	)

	const clickDifferentStaffInDifferentStaffsPopup = useCallback(
		(origin_staff_id: string, different_staff_id: string) => {
			sendEvent(cancelID, Event.CLICK_DIFFERENT_STAFF_POPUP, {
				origin_staff_id: origin_staff_id,
				different_staff_id: different_staff_id,
			})
		},
		[cancelID]
	)

	const confirmCreatingRereservationInDifferentStaffs = useCallback(
		(originStaffId: string, rereservedStaffId: string) => {
			sendEvent(
				cancelID,
				Event.CONFIRM_CREATING_RERESERVATION_IN_DIFFERENT_STAFFS,
				{
					origin_staff_id: originStaffId,
					rereserved_staff_id: rereservedStaffId,
				}
			)
		},
		[cancelID]
	)

	const closeDifferentStaffsPopup = useCallback(
		(originStaffId: string, differentStaffIds: string[]) => {
			sendEvent(cancelID, Event.CLOSE_DIFFERENT_STAFFS_POPUP, {
				origin_staff_id: originStaffId,
				different_staff_ids: differentStaffIds,
			})
		},
		[cancelID]
	)

	const openAbortPopup = useCallback(
		(recommendedSlots: { staffId: string; startTime: Date }[]) => {
			sendEvent(cancelID, Event.OPEN_ABORT_POPUP, {
				recommended_slots: recommendedSlots,
			})
		},
		[cancelID]
	)

	const clickRecommendedSlotInAbortPopup = useCallback(
		(startTime: Date, staffId: string) => {
			sendEvent(cancelID, Event.CLICK_RECOMMENDED_SLOT_IN_ABORT_POPUP, {
				start_time: startTime,
				staff_id: staffId,
			})
		},
		[cancelID]
	)

	const confirmCreatingRereservationInAbortPopup = useCallback(
		(startTime: Date, staffId: string) => {
			sendEvent(cancelID, Event.CONFIRM_CREATING_RERESERVATION_IN_ABORT_POPUP, {
				start_time: startTime,
				staff_id: staffId,
			})
		},
		[cancelID]
	)

	const clickAbortButton = useCallback(() => {
		sendEvent(cancelID, Event.CLICK_ABORT_BUTTON, {})
	}, [cancelID])

	const closeAbortPopup = useCallback(() => {
		sendEvent(cancelID, Event.CLOSE_ABORT_POPUP, {})
	}, [cancelID])

	const openAlreadyReservedPopup = useCallback(
		(startTime: Date) => {
			sendEvent(cancelID, Event.OPEN_ALREADY_RESERVED_POPUP, {
				start_time: startTime,
			})
		},
		[cancelID]
	)

	const closeAlreadyReservedPopup = useCallback(() => {
		sendEvent(cancelID, Event.CLOSE_ALREADY_RESERVED_POPUP, {})
	}, [cancelID])

	return {
		clickPrevSevenDays,
		clickNextSevenDays,
		fetchedReservableSlots,
		clickRereservableSlot,
		openCreatingReservationPopup,
		closeCreatingReservationPopup,
		fetchedCancelData,
		confirmCreatingRereservation,
		openDifferentStaffsPopup,
		clickDifferentStaffInDifferentStaffsPopup,
		confirmCreatingRereservationInDifferentStaffs,
		closeDifferentStaffsPopup,
		openAbortPopup,
		clickRecommendedSlotInAbortPopup,
		confirmCreatingRereservationInAbortPopup,
		clickAbortButton,
		closeAbortPopup,
		openAlreadyReservedPopup,
		closeAlreadyReservedPopup,
	}
}
